/* eslint-disable no-restricted-globals, no-unused-vars */
// ─────────────────────────────────────────────────────────────────────────
// SERGA // PERSONAL OS — store (mutable layer over data.js)
// The SERGA SHELL edits live here. Hybrid persistence:
//   • PROJECTS (+ their tasks/artifacts) are LIVE from the dashboard API —
//     edits hit the real backend, so they are NOT snapshotted to localStorage
//     (that would shadow real data). ZONES is derived from PROJECTS, also live.
//   • FINANCE / TRAVEL / EXTRA_ZONES have no backend yet, so they are MOCKED:
//     edited in place and mirrored to localStorage so a refresh keeps the draft.
// Everything mutates the SAME refs the rest of the app imports from data.js.
// ─────────────────────────────────────────────────────────────────────────
import { PROJECTS, FINANCE, INVEST, BUDGET, TRAVEL, ZONES, EXTRA_ZONES } from './data.js';

const KEY = 'serga-os-data';

// single mutable handle the shell reads/writes
export const DB = { PROJECTS, FINANCE, TRAVEL, ZONES, EXTRA_ZONES };

// ── snapshot / restore (mock entities only) ─────────────────────────────────
// VITALS / CAPTURE / SYSTEM are NOT here: VITALS (Mind) + CAPTURE are live
// (API-backed, like PROJECTS — edits hit the backend, so snapshotting would
// shadow real data), and SYSTEM telemetry is read-only/machine-written.
function snapshot() {
  return JSON.stringify({ FINANCE, TRAVEL, EXTRA_ZONES });
}

// replace the CONTENTS of the mock refs (keep the same array/object identities
// so live ESM bindings in cards.js / world.js stay valid)
function rehydrate(saved) {
  const swapArr = (arr, next) => { arr.length = 0; (next || []).forEach(x => arr.push(x)); };
  const swapObj = (obj, next) => { Object.keys(obj).forEach(k => delete obj[k]); Object.assign(obj, next || {}); };
  if (saved.FINANCE) swapObj(FINANCE, saved.FINANCE);
  if (saved.TRAVEL) swapArr(TRAVEL, saved.TRAVEL);
  if (saved.EXTRA_ZONES) swapArr(EXTRA_ZONES, saved.EXTRA_ZONES);
}

export function hasDraft() {
  try { return !!localStorage.getItem(KEY); } catch (e) { return false; }
}

// call ONCE before the model is built
export function loadDraft() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) rehydrate(JSON.parse(raw));
  } catch (e) { /* corrupt draft → fall back to seed */ }
}

export function persist() {
  try { localStorage.setItem(KEY, snapshot()); } catch (e) {}
}

export function reset() {
  try { localStorage.removeItem(KEY); } catch (e) {}
}

export function serialize() {
  return JSON.stringify({ FINANCE, TRAVEL, EXTRA_ZONES }, null, 2);
}

// ── helpers the shell uses to resolve / mutate nodes ────────────────────────
const slug = s => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 24) || 'node';
export function uniqueId(base, taken) {
  let id = slug(base), n = 2;
  while (taken.includes(id)) id = slug(base) + '-' + (n++);
  return id;
}

// every node id currently in play (for collision checks)
export function allNodeIds() {
  return [
    ...PROJECTS.map(p => p.id),
    ...TRAVEL.map(t => t.id),
    ...EXTRA_ZONES.flatMap(z => (z.nodes || []).map(n => n.id)),
  ];
}
export function allZoneIds() {
  return [...ZONES.map(z => z.id), ...EXTRA_ZONES.map(z => z.id)];
}

// is this the live, API-backed project list (vs a mock extra-zone node)?
export function isLiveProject(data) {
  return PROJECTS.includes(data);
}

// resolve a world card id ('p-czech' / 'tr-bosnia' / 'fin-runway' / 'z-…') to
// the live data object + a label describing what fields it accepts.
export function nodeByCardId(id) {
  if (!id) return null;
  if (id.startsWith('p-')) {
    const key = id.slice(2);
    let data = PROJECTS.find(p => p.id === key);
    if (!data) for (const z of EXTRA_ZONES) { const f = (z.nodes || []).find(n => n.id === key); if (f) { data = f; break; } }
    return data ? { kind: 'project', data, cardId: id } : null;
  }
  if (id.startsWith('tr-')) {
    const data = TRAVEL.find(t => t.id === id.slice(3));
    return data ? { kind: 'travel', data, cardId: id } : null;
  }
  // fin-invest reads the live portfolio (INVEST, a live ESM binding swapped by
  // setLiveInvestments); fin-runway is still the mock runway snapshot.
  if (id === 'fin-invest') return { kind: 'invest', data: INVEST, cardId: id };
  if (id === 'fin-budget') return { kind: 'budget', data: BUDGET, cardId: id };
  if (id === 'fin-runway') return { kind: 'finance', data: FINANCE, cardId: id };
  if (id.startsWith('z-')) {
    const key = id.slice(2);
    const data = ZONES.find(z => z.id === key) || EXTRA_ZONES.find(z => z.id === key);
    return data ? { kind: 'zone', data, cardId: id } : null;
  }
  return null;
}
